<template>
  <div class="modal-ai-setup-name-section">
    <BaseSubHeading
      size="large"
      weight="500"
      class="modal-ai-setup-name-section__label"
    >
      What's your favorite name?
    </BaseSubHeading>

    <BorderedInput
      id="ai-name"
      :modelValue="aiName"
      className="modal-ai-setup-name-section__input"
      placeholder="Give your AI a name"
      :rules="aiNameRules"
      :maxLength="AI_NAME_MAX_LENGTH"
      :validateOnChange="false"
      :validateOnModelUpdate="false"
      :validateOnInput="false"
      @update:modelValue="onAiNameUpdate"
    />

    <Field
      v-slot="{ meta, errorMessage, handleChange }"
      as="div"
      class="modal-ai-setup-name-section__gender"
      name="aiGender"
      :rules="genderRules"
      :modelValue="gender"
    >
      <button
        v-for="option in genderOptions"
        :key="option"
        type="button"
        class="modal-ai-setup-name-section__gender-button"
        :class="{ 'is-active': gender === option }"
        role="radio"
        :aria-checked="gender === option"
        @click="onSelectGender(option, handleChange)"
      >
        {{ $t(`genderLabel.${option}`) }}
      </button>
      <span
        v-if="!meta.valid && errorMessage"
        class="error-text error-text-small modal-ai-setup-name-section__gender-error"
      >
        AI gender is required
      </span>
    </Field>
    <BaseSubHeading
      size="medium"
      weight="400"
      class="modal-ai-setup-name-section__message"
      color="monochrome-05"
      darkColor="monochrome-04"
    >
      Please have your AI provider API key ready to <br />
      complete the set up.
    </BaseSubHeading>
  </div>
</template>

<script setup lang="ts">
import { Field } from "vee-validate"
import { computed } from "vue"

import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"
import BorderedInput from "@/components/Form/BorderedInput.vue"

import { AI_NAME_MAX_LENGTH } from "@/helpers/validationHelper"
import { AiGender } from "@/models"
import validators from "@/utils/validators"

defineProps({
  aiName: {
    type: String,
    default: ""
  },
  gender: {
    type: String as () => AiGender,
    default: ""
  },
  genderOptions: {
    type: Array as () => AiGender[],
    default: () => []
  }
})

const genderRules = computed(() => validators.isRequired("AI gender") as string)

const aiNameRules = (value: string) => {
  const stringValue = String(value ?? "").trim()

  if (!stringValue) {
    return "Name is required"
  }

  return stringValue.length > AI_NAME_MAX_LENGTH ? "Name is too long" : true
}

const emit = defineEmits<{
  (e: "update:aiName", value: string): void
  (e: "update:gender", value: AiGender): void
}>()

const onSelectGender = (option: AiGender, handleChange: (value: AiGender) => void) => {
  emit("update:gender", option)
  handleChange(option)
}

const onAiNameUpdate = ({ value }: { value: string }) => {
  emit("update:aiName", value?.trim() ?? "")
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/utils/variables/colors";
@import "@/assets/scss/utils/variables/fonts";
@import "@/assets/scss/utils/mixins/_flex";
@import "@/assets/scss/utils/typography-v2";

.modal-ai-setup-name-section {
  @include flex($direction: column);

  width: 400px;
  padding-top: 80px;

  @media (max-width: 1536px) {
    padding-top: 72px;
  }

  &__label {
    @include body-large-500;

    margin-bottom: var(--step2);
    color: var(--monochrome-07);
  }

  &__gender {
    @include flex($justify: center, $align: center);

    position: relative;
    gap: var(--step2);
  }

  &__gender-button {
    @include body-medium-400;

    padding: var(--step2) var(--step4);
    border: 1px solid var(--monochrome-02);
    border-radius: 6px;
    box-shadow: var(--center-01);
    color: var(--monochrome-06);
    cursor: pointer;

    &.is-active {
      border-color: var(--accent-02);
      box-shadow: var(--center-02);
    }
  }

  &__gender-error {
    right: 50%;
    transform: translateX(50%);
  }

  &__message {
    margin: auto auto 0;
  }
}

:deep(.modal-ai-setup-name-section__input) {
  margin-bottom: var(--step6);
}

.dark {
  .modal-ai-setup-name-section {
    &__gender-button {
      border-color: var(--monochrome-30);
      background: var(--monochrome-18);

      &.is-active {
        border-color: var(--accent-200);
      }
    }
  }
}
</style>
